import React, { useEffect, useRef, useState } from "react";
import { getShopFromUrl } from "../utils/shop";

const SHOPIFY_CLIENT_ID = "341d460221e28834843ca2b10e3b98f0";
const RENTAL_BLOCK_HANDLE = "rental-booking-widget";
const RENTAL_CART_EMBED_HANDLE = "rental-cart-pricing-embed";

function UploadIcon({ size = 12 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Toggle({ pressed, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        width: "56px",
        height: "32px",
        borderRadius: "999px",
        border: "none",
        background: pressed ? "#155dfc" : "#d1d5db",
        cursor: "pointer",
        position: "relative",
        padding: 0,
        boxShadow: pressed ? "0 4px 10px rgba(21, 93, 252, 0.28)" : "0 2px 6px rgba(15, 23, 42, 0.08)",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "4px",
          left: pressed ? "28px" : "4px",
          width: "24px",
          height: "24px",
          borderRadius: "999px",
          background: "#ffffff",
          transition: "left 0.2s ease",
        }}
      />
    </button>
  );
}

function StatusBadge({ daysRemaining, isExpired }) {
  const remaining = Number(daysRemaining) || 0;

  if (isExpired || remaining <= 0) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "10px",
          padding: "10px 14px",
          background: "#fee2e2",
          border: "1px solid #f87171",
          color: "#b91c1c",
          fontWeight: 700,
          fontSize: "15px",
          boxShadow: "0 2px 8px rgba(220, 38, 38, 0.15)",
        }}
      >
        ⚠️ License Expired
      </span>
    );
  }

  const isExpiring = remaining <= 30;

  if (isExpiring) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "10px",
          padding: "10px 14px",
          background: "#fee2e2",
          border: "1px solid #f87171",
          color: "#dc2626",
          fontWeight: 700,
          fontSize: "15px",
          boxShadow: "0 2px 8px rgba(220, 38, 38, 0.12)",
        }}
      >
        ⚠️ {remaining} {remaining === 1 ? "day" : "days"} remaining
      </span>
    );
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        borderRadius: "10px",
        padding: "10px 14px",
        background: "#dcfce7",
        border: "1px solid #86efac",
        color: "#15803d",
        fontWeight: 700,
        fontSize: "15px",
      }}
    >
      ✓ {remaining} {remaining === 1 ? "day" : "days"} remaining
    </span>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="30" height="30" fill="none" aria-hidden="true">
      <path d="M12 3.5 5 6.5v5.2c0 4.6 3 8.6 7 9.9 4-1.3 7-5.3 7-9.9V6.5L12 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="m8.6 12.3 2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="14" y="4" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="4" y="14" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
      <rect x="14" y="14" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function CubeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path d="m12 3 7 4v10l-7 4-7-4V7l7-4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 3v14" stroke="currentColor" strokeWidth="1.8" />
      <path d="m5 7 7 4 7-4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function PaletteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path
        d="M12 4a8 8 0 1 0 0 16h1.7c1 0 1.8-.8 1.8-1.8 0-.9-.7-1.7-1.6-1.7H13c-1.1 0-2-.9-2-2s.9-2 2-2h2.9A2.9 2.9 0 0 0 18.8 9C17.7 6.1 15.2 4 12 4Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="9" r="1" fill="currentColor" />
      <circle cx="7" cy="13" r="1" fill="currentColor" />
      <circle cx="9.8" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path d="M10.2 3.6h3.6l.5 2a6.7 6.7 0 0 1 1.5.8l1.9-1 2.5 2.5-1 1.9c.3.5.6 1 .8 1.5l2 .5v3.6l-2 .5a6.7 6.7 0 0 1-.8 1.5l1 1.9-2.5 2.5-1.9-1a6.7 6.7 0 0 1-1.5.8l-.5 2h-3.6l-.5-2a6.7 6.7 0 0 1-1.5-.8l-1.9 1-2.5-2.5 1-1.9a6.7 6.7 0 0 1-.8-1.5l-2-.5v-3.6l2-.5a6.7 6.7 0 0 1 .8-1.5l-1-1.9L6.2 5.4l1.9 1a6.7 6.7 0 0 1 1.5-.8l.5-2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="12" cy="12" r="3.1" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <rect x="4" y="5" width="16" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4 9h16" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 3.8v2.4M16 3.8v2.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8.2 13h7.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path d="M4.5 5h2l1.8 9h9.6l1.6-6H8.1" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="10" cy="19" r="1.6" fill="currentColor" />
      <circle cx="17" cy="19" r="1.6" fill="currentColor" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" aria-hidden="true">
      <path d="M12 12.2a4.3 4.3 0 1 0-4.3-4.3A4.3 4.3 0 0 0 12 12.2Z" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

const PAGE_STYLE = {
  minHeight: "100vh",
  padding: "10px",
  boxSizing: "border-box",
  background: "#eef3fb",
  display: "flex",
  justifyContent: "center",
};

const FRAME_STYLE = {
  height: "calc(100vh - 20px)",
  width: "min(1100px, calc(100vw - 20px))",
  borderRadius: "22px",
  overflow: "hidden",
  background: "#f6f9ff",
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.12)",
  display: "flex",
  flexDirection: "column",
};

const HEADER_STYLE = {
  minHeight: "88px",
  padding: "14px 22px 14px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "18px",
  background: "linear-gradient(135deg, #203f9f 0%, #244ab8 55%, #173d9c 100%)",
  color: "#ffffff",
};

const HEADER_LEFT_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
  minWidth: 0,
};

const LOGO_CONTAINER_STYLE = {
  position: "relative",
  width: "108px",
  height: "46px",
  borderRadius: "8px",
  background: "#ffffff",
  padding: "4px 8px",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
};

const LOGO_IMAGE_STYLE = {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
};

const UPLOAD_BADGE_STYLE = {
  position: "absolute",
  bottom: "-6px",
  right: "-6px",
  width: "22px",
  height: "22px",
  borderRadius: "999px",
  background: "#155dfc",
  color: "#ffffff",
  border: "2px solid #ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
  padding: 0,
  transition: "transform 0.15s ease, background-color 0.15s ease",
};

const RESET_BADGE_STYLE = {
  position: "absolute",
  top: "-6px",
  right: "-6px",
  width: "18px",
  height: "18px",
  borderRadius: "999px",
  background: "#ef4444",
  color: "#ffffff",
  border: "2px solid #ffffff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
  padding: 0,
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: 1,
};

const HEADER_DIVIDER_STYLE = {
  width: "1px",
  height: "50px",
  background: "rgba(255, 255, 255, 0.28)",
};

const HEADER_COPY_STYLE = { display: "grid", gap: "2px" };
const HEADER_TITLE_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "22px",
  fontWeight: 700,
  letterSpacing: "-0.03em",
  lineHeight: 1,
};
const HEADER_SUBTITLE_STYLE = { fontSize: "13px", color: "rgba(255,255,255,0.94)" };
const HEADER_RIGHT_STYLE = { display: "flex", alignItems: "center", gap: "12px" };
const AVATAR_STYLE = {
  width: "46px",
  height: "46px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.18)",
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const CONTENT_STYLE = {
  padding: "14px 28px 14px",
  display: "grid",
  gap: "16px",
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
};
const CARD_STYLE = { background: "#ffffff", borderRadius: "18px", boxShadow: "0 8px 24px rgba(15,23,42,0.08)" };
const LICENSE_STYLE = { ...CARD_STYLE, padding: "16px 20px" };
const LICENSE_LEFT_STYLE = { display: "flex", alignItems: "center", gap: "14px" };
const ICON_TILE_STYLE = {
  width: "68px",
  height: "68px",
  borderRadius: "18px",
  background: "#e6eeff",
  color: "#1d4ed8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
};
const LICENSE_TITLE_STYLE = { fontSize: "16px", fontWeight: 700, color: "#182146" };
const LICENSE_SUBTITLE_STYLE = { fontSize: "14px", color: "#4e5f95", marginTop: "4px" };
const LICENSE_RIGHT_STYLE = { display: "flex", alignItems: "center", gap: "14px" };
const EYE_BUTTON_STYLE = {
  width: "42px",
  height: "42px",
  borderRadius: "999px",
  border: "none",
  background: "#eef3ff",
  color: "#3555a5",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};
const SECTION_HEADER_STYLE = { display: "flex", alignItems: "center", gap: "14px" };
const SECTION_ICON_STYLE = {
  width: "46px",
  height: "46px",
  borderRadius: "14px",
  background: "#dfe9ff",
  color: "#1d4ed8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const SECTION_TITLE_STYLE = { fontSize: "19px", fontWeight: 700, color: "#182146" };
const SECTION_SUBTITLE_STYLE = { fontSize: "13px", color: "#4e5f95", marginTop: "3px" };
const FEATURE_GRID_STYLE = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" };
const FEATURE_CARD_STYLE = {
  ...CARD_STYLE,
  padding: "14px 16px",
  position: "relative",
  overflow: "hidden",
};
const FEATURE_ACCENT_STYLE = {
  position: "absolute",
  inset: "0 auto 0 0",
  width: "4px",
  background: "linear-gradient(180deg, #2563eb 0%, #4f46e5 100%)",
};
const FEATURE_TOP_STYLE = { display: "flex", alignItems: "center", gap: "10px" };
const FEATURE_ICON_STYLE = {
  width: "50px",
  height: "50px",
  borderRadius: "14px",
  background: "#e7efff",
  color: "#1d4ed8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const FEATURE_TITLE_STYLE = { fontSize: "14px", fontWeight: 700, color: "#182146" };
const FEATURE_DESC_STYLE = { fontSize: "11px", color: "#4e5f95", marginTop: "2px" };
const FEATURE_BOTTOM_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "10px",
  paddingTop: "8px",
  borderTop: "1px solid #e7ebf3",
};
const FEATURE_ENABLED_STYLE = { display: "inline-flex", alignItems: "center", gap: "10px", color: "#182146", fontSize: "12px", fontWeight: 600 };
const THEME_STYLE = { display: "grid", gap: "12px" };
const THEME_GRID_STYLE = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px" };
const THEME_CARD_STYLE = {
  ...CARD_STYLE,
  padding: "14px 16px",
  position: "relative",
  overflow: "hidden",
};
const THEME_ACCENT_STYLE = {
  position: "absolute",
  inset: "0 auto 0 0",
  width: "4px",
  background: "linear-gradient(180deg, #2563eb 0%, #4f46e5 100%)",
};
const THEME_TOP_STYLE = { display: "flex", alignItems: "center", gap: "10px" };
const THEME_TITLE_STYLE = { fontSize: "14px", fontWeight: 700, color: "#182146" };
const THEME_DESC_STYLE = { fontSize: "11px", color: "#4e5f95", marginTop: "2px" };
const THEME_BOTTOM_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "10px",
  paddingTop: "8px",
  borderTop: "1px solid #e7ebf3",
};
const THEME_ENABLED_STYLE = { display: "inline-flex", alignItems: "center", gap: "10px", color: "#182146", fontSize: "12px", fontWeight: 600 };
const THEME_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid #d1d5db",
  borderRadius: "10px",
  height: "32px",
  padding: "0 12px",
  background: "#ffffff",
  color: "#182146",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
};
const THEME_BUTTON_PRIMARY_STYLE = { ...THEME_BUTTON_STYLE, background: "#202020", color: "#ffffff", borderColor: "#202020" };
const THEME_ICON_STYLE = {
  width: "44px",
  height: "44px",
  borderRadius: "13px",
  background: "#dfe9ff",
  color: "#1d4ed8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const SIGNOUT_WRAP_STYLE = { display: "flex", justifyContent: "center", marginTop: "0" };

function SignOutIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M10 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m14 8 4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 12H9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default function Dashboard({ license, onNavigate, onLogout }) {
  const [licenseInfo, setLicenseInfo] = useState(() => ({
    licenseKey: license?.licenseKey ?? "",
    productCode: license?.productCode ?? "",
    accountId: license?.accountId ?? "",
    licenseUrl: license?.licenseUrl ?? "",
    daysRemaining: Number(license?.remainingDays ?? license?.daysRemaining ?? 0),
    planEndDate: license?.planEndDate ?? "",
  }));
  const [features, setFeatures] = useState({
    standardProducts: true,
    productVariants: true,
  });
  const [showLicenseDetails, setShowLicenseDetails] = useState(false);
  const [themeBookingEnabled, setThemeBookingEnabled] = useState(false);
  const [themeProfileEnabled, setThemeProfileEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customLogo, setCustomLogo] = useState(() => {
    try {
      return localStorage.getItem("rental_admin_custom_logo") || "/netscore-logo.png";
    } catch {
      return "/netscore-logo.png";
    }
  });
  const fileInputRef = useRef(null);

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      window.alert("Please upload a valid image file (PNG, JPG, SVG, WebP, etc.)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (!result) return;

      if (file.type === "image/svg+xml" || file.size < 300 * 1024) {
        try {
          localStorage.setItem("rental_admin_custom_logo", result);
        } catch (err) {
          console.warn("Could not save logo to localStorage:", err);
        }
        setCustomLogo(result);
      } else {
        const img = new Image();
        img.onload = () => {
          const maxDim = 400;
          let { width, height } = img;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            const compressedDataUrl = canvas.toDataURL(file.type === "image/png" ? "image/png" : "image/jpeg", 0.9);
            try {
              localStorage.setItem("rental_admin_custom_logo", compressedDataUrl);
            } catch (err) {
              console.warn("Could not save logo to localStorage:", err);
            }
            setCustomLogo(compressedDataUrl);
          } else {
            setCustomLogo(result);
          }
        };
        img.src = result;
      }
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const handleResetLogo = (e) => {
    e.stopPropagation();
    try {
      localStorage.removeItem("rental_admin_custom_logo");
    } catch {}
    setCustomLogo("/netscore-logo.png");
  };

  useEffect(() => {
    const fetchLicenseInfo = async () => {
      try {
        const response = await fetch("/api/admin/license/status");
        if (response.ok) {
          const data = await response.json();
          setLicenseInfo({
            licenseKey: data.licenseKey ?? "Net-TWVtNjk2OjMvMTgvMjAyNDpMWVBF",
            productCode: data.productCode ?? "LYPE",
            accountId: data.accountId ?? "TSTDRV2041352",
            licenseUrl: data.licenseUrl ?? "https://license.netscoretech.com/api/Account/GetLicenseDetails",
            daysRemaining: Number(data.remainingDays ?? data.daysRemaining ?? 0),
            planEndDate: data.planEndDate ?? "2026-09-15",
          });
        }
      } catch (error) {
        console.error("Failed to fetch license info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLicenseInfo();
  }, []);

  useEffect(() => {
    const fetchThemeSettings = async () => {
      const shop = getShopFromUrl();
      if (!shop) return;

      try {
        const bookingResponse = await fetch(`/api/admin/booking-settings?shop=${encodeURIComponent(shop)}`);
        if (bookingResponse.ok) {
          const bookingData = await bookingResponse.json();
          setThemeBookingEnabled(Boolean(bookingData?.rentalBookingEnabled));
          setThemeProfileEnabled(Boolean(bookingData?.rentalProfileEnabled));
        }
      } catch (error) {
        console.error("Failed to fetch theme settings:", error);
      }
    };

    fetchThemeSettings();
  }, []);

  const toggleFeature = (feature) => {
    setFeatures((prev) => ({ ...prev, [feature]: !prev[feature] }));
  };

  const saveBookingSetting = async (enabled) => {
    const shop = getShopFromUrl();
    if (!shop) {
      window.alert("Shop domain is missing. Reopen the app from Shopify admin and try again.");
      return false;
    }

    try {
      const response = await fetch("/api/admin/booking-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop, enabled, settingType: "booking" }),
      });

      if (!response.ok) {
        throw new Error("Failed to save booking setting");
      }

      const data = await response.json();
      setThemeBookingEnabled(Boolean(data?.rentalBookingEnabled));
      return Boolean(data?.rentalBookingEnabled);
    } catch (error) {
      console.error("Failed to save booking setting:", error);
      window.alert("Unable to save booking block setting right now.");
      return false;
    }
  };

  const saveProfileSetting = async (enabled) => {
    const shop = getShopFromUrl();
    if (!shop) {
      window.alert("Shop domain is missing. Reopen the app from Shopify admin and try again.");
      return false;
    }

    try {
      const response = await fetch("/api/admin/booking-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop, enabled, settingType: "profile", profileEnabled: enabled }),
      });

      if (!response.ok) {
        throw new Error("Failed to save profile setting");
      }

      const data = await response.json();
      setThemeProfileEnabled(Boolean(data?.rentalProfileEnabled));
      return Boolean(data?.rentalProfileEnabled);
    } catch (error) {
      console.error("Failed to save profile setting:", error);
      window.alert("Unable to save profile setting right now.");
      return false;
    }
  };

  const handleLogout = async () => {
    try {
      const shop = getShopFromUrl();
      const query = shop ? `?shop=${encodeURIComponent(shop)}` : "";
      await fetch(`/api/logout${query}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop }),
      });
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.removeItem("license");
      if (typeof onLogout === "function") {
        onLogout();
      }
      window.location.replace(window.location.origin + window.location.pathname + (window.location.search || ""));
    }
  };

  const daysRemaining = Number(licenseInfo.daysRemaining) || 0;
  const isExpired = daysRemaining <= 0;
  const isExpiringSoon = !isExpired && daysRemaining <= 30;

  return (
    <div style={PAGE_STYLE}>
      <div style={FRAME_STYLE}>
        <header style={HEADER_STYLE}>
          <div style={HEADER_LEFT_STYLE}>
            <div
              style={LOGO_CONTAINER_STYLE}
              onClick={() => fileInputRef.current?.click()}
              title="Click or use the upload icon to change logo"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <img
                src={customLogo || "/netscore-logo.png"}
                alt="Logo"
                style={LOGO_IMAGE_STYLE}
                onError={(e) => {
                  e.currentTarget.src = "/netscore-logo.png";
                }}
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                style={{ display: "none" }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
                style={UPLOAD_BADGE_STYLE}
                title="Upload logo"
                aria-label="Upload logo"
              >
                <UploadIcon size={11} />
              </button>
              {customLogo && customLogo !== "/netscore-logo.png" && (
                <button
                  type="button"
                  onClick={handleResetLogo}
                  style={RESET_BADGE_STYLE}
                  title="Reset to default logo"
                  aria-label="Reset to default logo"
                >
                  ✕
                </button>
              )}
            </div>
            <div style={HEADER_DIVIDER_STYLE} />
            <div style={HEADER_COPY_STYLE}>
              <div style={HEADER_TITLE_STYLE}>
                <span style={{ fontSize: "18px", lineHeight: 1 }}>🎯</span>
                <span>Rental Configuration</span>
              </div>
              <div style={HEADER_SUBTITLE_STYLE}>Manage your rental products and license settings</div>
            </div>
          </div>

          <div style={HEADER_RIGHT_STYLE}>
            <StatusBadge daysRemaining={licenseInfo.daysRemaining} isExpired={isExpired} />
            <div style={AVATAR_STYLE} aria-hidden="true">
              <UserIcon />
            </div>
          </div>
        </header>

        <main style={CONTENT_STYLE}>
          <section
            style={{
              ...LICENSE_STYLE,
              border: isExpiringSoon || isExpired ? "1.5px solid #fca5a5" : "1px solid #e2e8f0",
              background: isExpiringSoon || isExpired ? "#fffcfc" : "#ffffff",
              boxShadow: isExpiringSoon || isExpired
                ? "0 8px 24px rgba(239, 68, 68, 0.10)"
                : "0 8px 24px rgba(15, 23, 42, 0.08)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px" }}>
              <div style={LICENSE_LEFT_STYLE}>
                <div
                  style={{
                    ...ICON_TILE_STYLE,
                    background: isExpiringSoon || isExpired ? "#fee2e2" : "#e6eeff",
                    color: isExpiringSoon || isExpired ? "#dc2626" : "#1d4ed8",
                  }}
                >
                  <ShieldIcon />
                </div>
                <div>
                  <div style={LICENSE_TITLE_STYLE}>License Information</div>
                  <div style={LICENSE_SUBTITLE_STYLE}>
                    {isExpiringSoon
                      ? "Your subscription is expiring soon. Please renew to keep all features."
                      : isExpired
                        ? "Your subscription has expired. Please renew immediately."
                        : "Your current subscription and account details"}
                  </div>
                </div>
              </div>

              <div style={LICENSE_RIGHT_STYLE}>
                <button
                  type="button"
                  onClick={() => setShowLicenseDetails((value) => !value)}
                  style={EYE_BUTTON_STYLE}
                  title={showLicenseDetails ? "Hide details" : "Show details"}
                >
                  <EyeIcon />
                </button>
                <StatusBadge daysRemaining={licenseInfo.daysRemaining} isExpired={isExpired} />
              </div>
            </div>

            {(isExpiringSoon || isExpired) && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "12px 18px",
                  borderRadius: "12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "14px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ fontSize: "22px" }}>⚠️</span>
                  <div>
                    <div style={{ color: "#991b1b", fontSize: "14px", fontWeight: 700 }}>
                      {isExpired
                        ? "License Expired"
                        : `License Expiring Soon (${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining)`}
                    </div>
                    <div style={{ color: "#b91c1c", fontSize: "12px", marginTop: "2px" }}>
                      {isExpired
                        ? "Your license subscription has ended. Please renew to keep rental features active."
                        : `Your license will expire on ${licenseInfo.planEndDate || "soon"}. Please renew within ${daysRemaining} days to avoid service disruption.`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showLicenseDetails && (
              <div style={{ marginTop: "22px", display: "grid", gap: "14px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px" }}>
                  {[
                    ["LICENSE KEY", licenseInfo.licenseKey],
                    ["PRODUCT CODE", licenseInfo.productCode],
                    ["ACCOUNT ID", licenseInfo.accountId],
                    ["EXPIRATION DATE", licenseInfo.planEndDate],
                  ].map(([label, value]) => {
                    const isExpTile = label === "EXPIRATION DATE" && (isExpiringSoon || isExpired);
                    return (
                      <div
                        key={label}
                        style={{
                          border: isExpTile ? "1px solid #fca5a5" : "1px solid #e5eaf3",
                          borderRadius: "12px",
                          background: isExpTile ? "#fef2f2" : "#f9fbff",
                          padding: "16px 18px",
                        }}
                      >
                        <div style={{ color: isExpTile ? "#b91c1c" : "#6b7280", fontSize: "12px", fontWeight: 700 }}>
                          {label}
                        </div>
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: isExpTile ? "#dc2626" : "#182146",
                            wordBreak: "break-all",
                          }}
                        >
                          {value || "-"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          <section style={{ display: "grid", gap: "14px" }}>
            <div style={SECTION_HEADER_STYLE}>
              <div style={SECTION_ICON_STYLE}>
                <GridIcon />
              </div>
              <div>
                <div style={SECTION_TITLE_STYLE}>Product Features</div>
                <div style={SECTION_SUBTITLE_STYLE}>Enable or disable features for different product types</div>
              </div>
            </div>

            <div style={FEATURE_GRID_STYLE}>
              <FeatureCard
                title="Standard Products"
                description="Basic rental items"
                icon={<CubeIcon />}
                enabled={features.standardProducts}
                onToggle={() => toggleFeature("standardProducts")}
              />
              <FeatureCard
                title="Product Variants"
                description="Sizes, colors, models"
                icon={<PaletteIcon />}
                enabled={features.productVariants}
                onToggle={() => toggleFeature("productVariants")}
              />
            </div>
          </section>

          <section style={THEME_STYLE}>
            <div style={SECTION_HEADER_STYLE}>
              <div style={THEME_ICON_STYLE}>
                <GearIcon />
              </div>
              <div>
                <div style={SECTION_TITLE_STYLE}>Theme Setup</div>
                <div style={SECTION_SUBTITLE_STYLE}>
                  Open the active theme editor and add the Rental Booking block to the product template.
                </div>
              </div>
            </div>

            <div style={THEME_GRID_STYLE}>
              <div style={THEME_CARD_STYLE}>
                <div style={THEME_ACCENT_STYLE} />
                <div style={THEME_TOP_STYLE}>
                  <div style={FEATURE_ICON_STYLE}>
                    <CalendarIcon />
                  </div>
                  <div>
                    <div style={THEME_TITLE_STYLE}>Enable Rental Information on Product Page</div>
                    <div style={THEME_DESC_STYLE}>Shopify will open the current theme editor and preselect your app block.</div>
                  </div>
                </div>
                <div style={THEME_BOTTOM_STYLE}>
                  <div style={THEME_ENABLED_STYLE}>
                    <span style={{ width: "22px", height: "22px", borderRadius: "999px", background: themeBookingEnabled ? "#16a34a" : "#9ca3af", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", lineHeight: 1 }}>
                      ✓
                    </span>
                    <span>{themeBookingEnabled ? "Enabled" : "Disabled"}</span>
                  </div>
                  <Toggle
                    pressed={themeBookingEnabled}
                    onChange={async () => {
                      const nextEnabled = !themeBookingEnabled;
                      await saveBookingSetting(nextEnabled);
                    }}
                  />
                </div>
              </div>

              <div style={THEME_CARD_STYLE}>
                <div style={THEME_ACCENT_STYLE} />
                <div style={THEME_TOP_STYLE}>
                  <div style={FEATURE_ICON_STYLE}>
                    <UserIcon />
                  </div>
                  <div>
                    <div style={THEME_TITLE_STYLE}>Enable Rental Information on Profile Page</div>
                    <div style={THEME_DESC_STYLE}>Show or hide the Rental Information tab and dashboard in customer profile.</div>
                  </div>
                </div>
                <div style={THEME_BOTTOM_STYLE}>
                  <div style={THEME_ENABLED_STYLE}>
                    <span style={{ width: "22px", height: "22px", borderRadius: "999px", background: themeProfileEnabled ? "#16a34a" : "#9ca3af", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", lineHeight: 1 }}>
                      ✓
                    </span>
                    <span>{themeProfileEnabled ? "Enabled" : "Disabled"}</span>
                  </div>
                  <Toggle
                    pressed={themeProfileEnabled}
                    onChange={async () => {
                      const nextEnabled = !themeProfileEnabled;
                      await saveProfileSetting(nextEnabled);
                    }}
                  />
                </div>
              </div>
            </div>
          </section>

          <div style={SIGNOUT_WRAP_STYLE}>
            <button
              type="button"
              onClick={handleLogout}
              style={{
                border: "1px solid #ff5b57",
                color: "#ff3b30",
                background: "#ffffff",
                borderRadius: "12px",
                height: "44px",
                padding: "0 24px",
                fontSize: "15px",
                fontWeight: 800,
                cursor: "pointer",
                boxShadow: "0 6px 18px rgba(255, 59, 48, 0.12)",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <SignOutIcon />
              Sign Out
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}

function FeatureCard({ title, description, icon, enabled, onToggle }) {
  return (
    <div style={FEATURE_CARD_STYLE}>
      <div style={FEATURE_ACCENT_STYLE} />
      <div style={FEATURE_TOP_STYLE}>
        <div style={FEATURE_ICON_STYLE}>{icon}</div>
        <div>
          <div style={FEATURE_TITLE_STYLE}>{title}</div>
          <div style={FEATURE_DESC_STYLE}>{description}</div>
        </div>
      </div>

      <div style={FEATURE_BOTTOM_STYLE}>
        <div style={FEATURE_ENABLED_STYLE}>
          <span style={{ width: "22px", height: "22px", borderRadius: "999px", background: "#16a34a", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px", lineHeight: 1 }}>
            ✓
          </span>
          <span>{enabled ? "Enabled" : "Disabled"}</span>
        </div>
        <Toggle pressed={enabled} onChange={onToggle} />
      </div>
    </div>
  );
}
